import { ICellValue, IColumnarColumnPayload, ITableRowCompressed } from 'types'
import {
  COLUMNAR_MODE_ALL_NULL,
  COLUMNAR_MODE_RAW,
  COLUMNAR_MODE_TEXT_JOINED,
  decodeDictIndices,
  decodeJoinedText
} from 'components/spreadsheet/helpers/columnar-stream'

export interface ColumnConfig {
  index: number
  kind: string
}

// Column kinds that use dictionary encoding (low-cardinality)
const DICTIONARY_KINDS = new Set(['select', 'checkbox', 'tag', 'text', 'email', 'phone', 'date', 'datetime'])

// Column kinds that use typed Float64Array (numeric)
const TYPED_KINDS = new Set(['integer', 'float', 'percentage', 'duration'])

// Link and variable (strings the server never dictionary-encodes) use JoinedTextColumn.
// Everything else falls through to SparseColumn: multiselect, multilink, attachment, vote, json

/**
 * Serialize a cell value to a string suitable as a Map key.
 * null -> '\0null', boolean -> '\0true'/'\0false', else String(value)
 */
function serializeValue(value: ICellValue): string {
  if (value === null || value === undefined) return '\0null'
  if (typeof value === 'boolean') return value ? '\0true' : '\0false'
  return String(value)
}

// --- Column strategy interfaces ---

interface IColumnStrategy {
  get(rowIndex: number): ICellValue
  set(rowIndex: number, value: ICellValue): void
  tombstone(rowIndex: number): void
  grow(newCapacity: number): void
  setDictIndex?(rowIndex: number, dictIndex: number): void
  setDictIndices?(startRowIndex: number, dictIndices: Uint16Array | Uint32Array): void
  appendDictEntries?(entries: ICellValue[]): void
  appendRawValues?(startRowIndex: number, values: ICellValue[]): void
  appendJoinedBatch?(startRowIndex: number, joined: string, nullPositions: number[]): void
}

/**
 * Text values of one streamed batch kept as the single NUL-joined string the server sent, with
 * the start offset of each value. A cell's string is cut out only when it is read.
 *
 * This is the difference between a million-row URL column costing ~115 MB of heap as a million
 * small strings (each a GC-visible object) and ~90 MB as ~45 large strings plus offsets.
 */
interface JoinedTextSegment {
  startRowIndex: number
  count: number
  joined: string
  // offsets[i] .. offsets[i + 1] - 1 is value i; the separator sits at offsets[i + 1] - 1.
  offsets: Uint32Array
  // 1 where the value is null rather than ''.
  nullMask: Uint8Array | null
}

const buildJoinedTextSegment = (startRowIndex: number, joined: string, nullPositions: number[]): JoinedTextSegment => {
  // Count the separators first so the offsets array is allocated once.
  let count = 1
  for (let at = joined.indexOf('\u0000'); at !== -1; at = joined.indexOf('\u0000', at + 1)) count++
  const offsets = new Uint32Array(count + 1)
  let value = 1
  for (let at = joined.indexOf('\u0000'); at !== -1; at = joined.indexOf('\u0000', at + 1)) offsets[value++] = at + 1
  offsets[count] = joined.length + 1
  let nullMask: Uint8Array | null = null
  if (nullPositions.length > 0) {
    nullMask = new Uint8Array(count)
    for (let i = 0; i < nullPositions.length; i++) nullMask[nullPositions[i]] = 1
  }
  return { startRowIndex, count, joined, offsets, nullMask }
}

/** Binary search for the segment holding `rowIndex`, or null. Segments are appended in row order. */
const findJoinedSegment = (segments: JoinedTextSegment[], rowIndex: number): JoinedTextSegment | null => {
  let lo = 0
  let hi = segments.length - 1
  while (lo <= hi) {
    const mid = (lo + hi) >>> 1
    const segment = segments[mid]
    if (rowIndex < segment.startRowIndex) hi = mid - 1
    else if (rowIndex >= segment.startRowIndex + segment.count) lo = mid + 1
    else return segment
  }
  return null
}

const readJoinedSegment = (segment: JoinedTextSegment, rowIndex: number): string | null => {
  const i = rowIndex - segment.startRowIndex
  if (segment.nullMask && segment.nullMask[i]) return null
  return segment.joined.substring(segment.offsets[i], segment.offsets[i + 1] - 1)
}

/**
 * DictionaryColumn — for string/low-cardinality columns.
 * Uses a Uint16Array of indices into a dictionary of unique values.
 * Index 0 = null.
 * When unique values exceed 65535, widens to Uint32Array (supports ~4B unique values)
 * instead of falling back to expensive per-cell storage.
 */
// In a widened (Uint32) dictionary column, an index with this bit set means "the value is in the
// column's joined-text segments for this row" rather than in the dictionary.
const JOINED_FLAG = 0x80000000

class DictionaryColumn implements IColumnStrategy {
  private indices: Uint16Array | Uint32Array
  private dictionary: ICellValue[]
  private valueToIndex: Map<string, number> | null // null during streaming, built lazily on first set()
  private isWide = false
  // Raw (non-dictionary) batches of a high-cardinality column, kept joined; see JoinedTextSegment.
  private joined: JoinedTextSegment[] = []

  constructor(capacity: number) {
    this.indices = new Uint16Array(capacity) // all zeros = null
    this.dictionary = [null] // index 0 is the null sentinel
    this.valueToIndex = null // lazy — not built until first set() call
  }

  get(rowIndex: number): ICellValue {
    const idx = this.indices[rowIndex]
    if (idx >= JOINED_FLAG) {
      const segment = findJoinedSegment(this.joined, rowIndex)
      return segment ? readJoinedSegment(segment, rowIndex) : null
    }
    return this.dictionary[idx]
  }

  /**
   * Keep a raw text batch as the one joined string the server sent, marking its rows so reads cut
   * the value out on demand instead of holding a string object per row.
   */
  appendJoinedBatch(startRowIndex: number, joined: string, nullPositions: number[]): void {
    if (!this.isWide) this.widen()
    const segment = buildJoinedTextSegment(startRowIndex, joined, nullPositions)
    this.joined.push(segment)
    const indices = this.indices
    for (let i = 0; i < segment.count; i++) {
      indices[startRowIndex + i] = segment.nullMask && segment.nullMask[i] ? 0 : JOINED_FLAG
    }
  }

  /** Write a server-provided dictionary index directly into the typed array */
  setDictIndex(rowIndex: number, dictIndex: number): void {
    this.indices[rowIndex] = dictIndex
  }

  /** Bulk-write a batch of server-provided dictionary indices starting at `startRowIndex`. */
  setDictIndices(startRowIndex: number, dictIndices: Uint16Array | Uint32Array): void {
    // The server only sends uint32 indices once its dictionary outgrew uint16, so widen to match.
    if (!this.isWide && dictIndices instanceof Uint32Array) {
      this.widen()
    }
    this.indices.set(dictIndices, startRowIndex)
  }

  /**
   * Bulk-write a batch of values the server sent raw (it stopped dictionary-encoding a
   * high-cardinality column). Each non-null value becomes a fresh dictionary entry — no
   * de-duplication, which is the point: a Map lookup per cell is what the server just avoided.
   */
  appendRawValues(startRowIndex: number, values: ICellValue[]): void {
    if (!this.isWide && this.dictionary.length + values.length > 65536) {
      this.widen()
    }
    let next = this.dictionary.length
    for (let i = 0; i < values.length; i++) {
      const value = values[i]
      if (value === null || value === undefined) {
        this.indices[startRowIndex + i] = 0
      } else {
        this.dictionary.push(value)
        this.indices[startRowIndex + i] = next++
      }
    }
    this.valueToIndex = null
  }

  /** Append new dictionary entries from a server batch. Triggers widen if needed. */
  appendDictEntries(entries: ICellValue[]): void {
    if (!this.isWide && this.dictionary.length + entries.length > 65536) {
      this.widen()
    }
    this.dictionary.push(...entries)
    // Invalidate valueToIndex cache since dictionary changed
    if (this.valueToIndex) this.valueToIndex = null
  }

  /** Lazily rebuild valueToIndex from the current dictionary */
  private ensureValueToIndex(): Map<string, number> {
    if (!this.valueToIndex) {
      this.valueToIndex = new Map()
      for (let i = 0; i < this.dictionary.length; i++) {
        this.valueToIndex.set(serializeValue(this.dictionary[i]), i)
      }
    }
    return this.valueToIndex
  }

  set(rowIndex: number, value: ICellValue): void {
    const vtoi = this.ensureValueToIndex()
    const key = serializeValue(value)
    let dictIndex = vtoi.get(key)

    if (dictIndex === undefined) {
      if (!this.isWide && this.dictionary.length >= 65536) {
        this.widen()
      }
      dictIndex = this.dictionary.length
      this.dictionary.push(value)
      vtoi.set(key, dictIndex)
    }

    this.indices[rowIndex] = dictIndex
  }

  tombstone(rowIndex: number): void {
    this.indices[rowIndex] = 0
  }

  grow(newCapacity: number): void {
    const TypedArray = this.isWide ? Uint32Array : Uint16Array
    const newIndices = new TypedArray(newCapacity)
    newIndices.set(this.indices)
    this.indices = newIndices
  }

  /** Widen indices from Uint16Array to Uint32Array to support >65535 unique values */
  private widen(): void {
    const wide = new Uint32Array(this.indices.length)
    wide.set(this.indices)
    this.indices = wide
    this.isWide = true
  }
}

/**
 * JoinedTextColumn — for string columns the server never dictionary-encodes (link, variable).
 * Streamed batches stay joined (see JoinedTextSegment); edits and non-joined values live in a
 * sparse override map.
 */
class JoinedTextColumn implements IColumnStrategy {
  private segments: JoinedTextSegment[] = []
  private overrides = new Map<number, ICellValue>()

  get(rowIndex: number): ICellValue {
    const override = this.overrides.get(rowIndex)
    if (override !== undefined) return override
    const segment = findJoinedSegment(this.segments, rowIndex)
    return segment ? readJoinedSegment(segment, rowIndex) : null
  }

  set(rowIndex: number, value: ICellValue): void {
    this.overrides.set(rowIndex, value === undefined ? null : value)
  }

  tombstone(rowIndex: number): void {
    this.overrides.set(rowIndex, null)
  }

  grow(): void {
    // Segments and overrides are not sized by capacity.
  }

  appendJoinedBatch(startRowIndex: number, joined: string, nullPositions: number[]): void {
    this.segments.push(buildJoinedTextSegment(startRowIndex, joined, nullPositions))
  }

  /**
   * Store a run of values as one joined segment, as a streamed batch would be, rather than as an
   * override per row. This is how a whole column is (re)built, e.g. when a column's kind changes
   * to link or variable; a million overrides in a Map would cost more than the strings they hold.
   */
  appendRawValues(startRowIndex: number, values: ICellValue[]): void {
    if (values.length === 0) return
    const parts: string[] = new Array(values.length)
    const nullPositions: number[] = []
    for (let i = 0; i < values.length; i++) {
      const value = values[i]
      if (value === null || value === undefined) {
        parts[i] = ''
        nullPositions.push(i)
      } else {
        // NUL separates the joined values; a cell cannot hold one (Postgres text rejects it).
        parts[i] = String(value).replaceAll('\u0000', '')
      }
    }
    this.appendJoinedBatch(startRowIndex, parts.join('\u0000'), nullPositions)
  }
}

/**
 * TypedColumn — for numeric columns.
 * Uses Float64Array. NaN = null (0 is a valid value).
 */
class TypedColumn implements IColumnStrategy {
  private data: Float64Array

  constructor(capacity: number) {
    this.data = new Float64Array(capacity)
    this.data.fill(NaN) // NaN = null
  }

  get(rowIndex: number): ICellValue {
    const v = this.data[rowIndex]
    return Number.isNaN(v) ? null : v
  }

  set(rowIndex: number, value: ICellValue): void {
    if (value === null || value === undefined) {
      this.data[rowIndex] = NaN
    } else {
      this.data[rowIndex] = Number(value)
    }
  }

  tombstone(rowIndex: number): void {
    this.data[rowIndex] = NaN
  }

  grow(newCapacity: number): void {
    const newData = new Float64Array(newCapacity)
    newData.fill(NaN)
    newData.set(this.data)
    this.data = newData
  }
}

/**
 * SparseColumn — fallback for complex/high-cardinality types.
 * Plain array storage.
 */
class SparseColumn implements IColumnStrategy {
  private data: (ICellValue | undefined)[]

  constructor(capacity: number) {
    this.data = new Array(capacity)
  }

  get(rowIndex: number): ICellValue {
    const v = this.data[rowIndex]
    return v === undefined ? null : v
  }

  set(rowIndex: number, value: ICellValue): void {
    this.data[rowIndex] = value
  }

  tombstone(rowIndex: number): void {
    this.data[rowIndex] = undefined
  }

  grow(newCapacity: number): void {
    // Extend array length
    this.data.length = newCapacity
  }
}

// String kinds the server sends raw rather than dictionary-encoded; stored joined, not per cell.
const JOINED_TEXT_KINDS = new Set(['link', 'variable'])

function createColumn(kind: string, capacity: number): IColumnStrategy {
  if (DICTIONARY_KINDS.has(kind)) return new DictionaryColumn(capacity)
  if (TYPED_KINDS.has(kind)) return new TypedColumn(capacity)
  if (JOINED_TEXT_KINDS.has(kind)) return new JoinedTextColumn()
  return new SparseColumn(capacity)
}

/**
 * CompressedColumnStore — column-oriented storage for spreadsheet cell data.
 *
 * Stores cell data in typed arrays organized by column, with three strategies:
 * - DictionaryColumn for low-cardinality string/boolean columns
 * - TypedColumn for numeric columns (Float64Array)
 * - SparseColumn for complex/high-cardinality types
 */
export class CompressedColumnStore {
  private columns: IColumnStrategy[]
  private dictColumnFlags: boolean[]
  private _capacity: number
  private _nextRowIndex: number

  constructor(initialCapacity: number, columnConfigs: ColumnConfig[]) {
    this._capacity = Math.max(initialCapacity, 1) // At least 1 to avoid edge cases with doubling
    this._nextRowIndex = initialCapacity

    // Place columns at their specified index positions (server header positions).
    // Gaps are filled with SparseColumn so out-of-bounds access from unmapped
    // server columns returns null instead of crashing.
    if (columnConfigs.length === 0) {
      this.columns = []
      this.dictColumnFlags = []
    } else {
      const maxIndex = columnConfigs.reduce((max, c) => Math.max(max, c.index), -1)
      this.columns = new Array<IColumnStrategy>(maxIndex + 1)
      this.dictColumnFlags = new Array(maxIndex + 1).fill(false)
      for (const config of columnConfigs) {
        this.columns[config.index] = createColumn(config.kind, this._capacity)
        if (DICTIONARY_KINDS.has(config.kind)) {
          this.dictColumnFlags[config.index] = true
        }
      }
      // Fill gaps with SparseColumn (for server columns not in the local view)
      for (let i = 0; i <= maxIndex; i++) {
        if (!this.columns[i]) {
          this.columns[i] = new SparseColumn(this._capacity)
        }
      }
    }
  }

  get nextRowIndex(): number {
    return this._nextRowIndex
  }

  get(rowIndex: number, columnIndex: number): ICellValue {
    const col = this.columns[columnIndex]
    return col ? col.get(rowIndex) : null
  }

  set(rowIndex: number, columnIndex: number, value: ICellValue): void {
    const col = this.columns[columnIndex]
    if (col) col.set(rowIndex, value)
  }

  isDictColumn(columnIndex: number): boolean {
    return this.dictColumnFlags[columnIndex] ?? false
  }

  setDictIndex(rowIndex: number, columnIndex: number, dictIndex: number): void {
    const col = this.columns[columnIndex]
    if (col?.setDictIndex) col.setDictIndex(rowIndex, dictIndex)
  }

  appendColumnDictEntries(columnIndex: number, entries: ICellValue[]): void {
    const col = this.columns[columnIndex]
    if (col?.appendDictEntries) col.appendDictEntries(entries)
  }

  /**
   * Bulk-ingest a streamed batch directly into column storage.
   * Operates on the internal column strategies without per-cell method dispatch
   * through CompressedColumnStore, saving ~1 dispatch + 1 array lookup per cell.
   */
  ingestBatch(data: ITableRowCompressed[], hasColumnId: boolean[], dictFlags: boolean[], startRowIndex: number): void {
    const cols = this.columns
    for (let i = 0; i < data.length; i++) {
      const row = data[i]
      const rowIdx = startRowIndex + i
      for (let j = 2; j < row.length; j++) {
        const ci = j - 2
        if (!hasColumnId[ci]) continue
        const col = cols[ci]
        if (dictFlags[ci]) {
          col.setDictIndex!(rowIdx, row[j] as number)
        } else {
          col.set(rowIdx, row[j] as ICellValue)
        }
      }
    }
  }

  /**
   * Bulk-ingest one `columnar`-format batch: each column arrives whole, as either a typed array
   * of dictionary indices or a list of raw values, and is written in one pass per column.
   */
  ingestColumnarBatch(
    columns: IColumnarColumnPayload[],
    hasColumnId: boolean[],
    startRowIndex: number,
    rowCount: number
  ): void {
    for (let ci = 0; ci < columns.length; ci++) {
      if (!hasColumnId[ci]) continue
      const col = this.columns[ci]
      if (!col) continue
      const [mode, payload] = columns[ci]
      // Every cell is null: the store already reads unset cells as null, so there is nothing to write.
      if (mode === COLUMNAR_MODE_ALL_NULL) continue
      if (mode === COLUMNAR_MODE_TEXT_JOINED && col.appendJoinedBatch) {
        const [joined, nullPositions] = payload as [string, number[]]
        col.appendJoinedBatch(startRowIndex, joined, nullPositions)
        continue
      }
      if (mode === COLUMNAR_MODE_RAW || mode === COLUMNAR_MODE_TEXT_JOINED) {
        const values =
          mode === COLUMNAR_MODE_TEXT_JOINED
            ? decodeJoinedText(payload as [string, number[]])
            : (payload as ICellValue[])
        if (col.appendRawValues) {
          col.appendRawValues(startRowIndex, values)
        } else {
          for (let i = 0; i < values.length; i++) col.set(startRowIndex + i, values[i])
        }
      } else if (col.setDictIndices) {
        col.setDictIndices(startRowIndex, decodeDictIndices(mode, payload as Uint8Array, rowCount))
      } else {
        // The server and the store agree on which kinds are dictionary-encoded; silently dropping
        // a column's data would be far worse than failing the load.
        throw new Error(`Column ${ci} is dictionary-encoded on the server but not in the store`)
      }
    }
  }

  addRow(): number {
    const rowIndex = this._nextRowIndex
    this._nextRowIndex++
    this.ensureCapacity(this._nextRowIndex)
    return rowIndex
  }

  addRows(count: number): number[] {
    const indices: number[] = []
    const startIndex = this._nextRowIndex
    for (let i = 0; i < count; i++) {
      indices.push(startIndex + i)
    }
    this._nextRowIndex = startIndex + count
    this.ensureCapacity(this._nextRowIndex)
    return indices
  }

  deleteRows(rowIndices: number[]): void {
    for (const rowIndex of rowIndices) {
      for (const column of this.columns) {
        column.tombstone(rowIndex)
      }
    }
  }

  addColumn(columnIndex: number, kind: string): void {
    const column = createColumn(kind, this._capacity)
    this.columns.splice(columnIndex, 0, column)
    this.dictColumnFlags.splice(columnIndex, 0, DICTIONARY_KINDS.has(kind))
  }

  deleteColumn(columnIndex: number): void {
    this.columns.splice(columnIndex, 1)
    this.dictColumnFlags.splice(columnIndex, 1)
  }

  /**
   * Replace the storage strategy for a column when its kind changes.
   * Reads all values from the old strategy, applies a transform, and writes
   * them into a new strategy matching the target kind.
   */
  replaceColumnKind(columnIndex: number, newKind: string, transform: (value: ICellValue) => ICellValue): void {
    const oldCol = this.columns[columnIndex]
    if (!oldCol) return

    const newCol = createColumn(newKind, this._capacity)
    if (newCol instanceof JoinedTextColumn) {
      // Build the column in bulk: a per-row set() on this strategy is an override-map entry.
      const values: ICellValue[] = new Array(this._nextRowIndex)
      for (let row = 0; row < this._nextRowIndex; row++) values[row] = transform(oldCol.get(row))
      newCol.appendRawValues(0, values)
    } else {
      for (let row = 0; row < this._nextRowIndex; row++) {
        newCol.set(row, transform(oldCol.get(row)))
      }
    }

    this.columns[columnIndex] = newCol
    this.dictColumnFlags[columnIndex] = DICTIONARY_KINDS.has(newKind)
  }

  getRowAsObject(rowIndex: number, indexToColumnId: string[]): Record<string, ICellValue> {
    const result: Record<string, ICellValue> = {}
    for (let i = 0; i < this.columns.length; i++) {
      const columnId = indexToColumnId[i]
      if (columnId !== undefined) {
        result[columnId] = this.columns[i].get(rowIndex)
      }
    }
    return result
  }

  ensureMinCapacity(size: number): void {
    this.ensureCapacity(size)
    if (this._nextRowIndex < size) {
      this._nextRowIndex = size
    }
  }

  private ensureCapacity(requiredCapacity: number): void {
    if (requiredCapacity <= this._capacity) return
    let newCapacity = this._capacity
    while (newCapacity < requiredCapacity) {
      newCapacity *= 2
    }
    for (const column of this.columns) {
      column.grow(newCapacity)
    }
    this._capacity = newCapacity
  }
}
